import React from 'react';
import styled from 'styled-components';
import { FaHardHat, FaHome, FaBuilding, FaIndustry, FaUserTie, FaMapMarkerAlt, FaPhone, FaEnvelope, FaCalendarAlt, FaStar } from 'react-icons/fa';
import DemoViewer from '../../components/DemoViewer';
import SEO from '../../components/SEO';

const ConstructoraDemo = styled.div`
  font-family: 'Roboto', sans-serif;
  color: #333;
`;

const HeroSection = styled.div`
  background-image: url('/servicios/tipo-cliente/constructora-hero.webp');
  background-size: cover;
  background-position: center;
  height: 600px;
  display: flex;
  align-items: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
  color: white;
`;

const CompanyName = styled.h3`
  font-size: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1rem;
  color: #ffc107;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  max-width: 700px;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  max-width: 600px;
  line-height: 1.6;
`;

const HeroButton = styled.button`
  background-color: #ffc107;
  color: #333;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
`;

const Section = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #ffc107;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
`;

const ServiceCard = styled.div`
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const ServiceImage = styled.div`
  height: 200px;
  background-size: cover;
  background-position: center;
`;

const ServiceContent = styled.div`
  padding: 2rem;
`;

const ServiceIcon = styled.div`
  color: #ffc107;
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ServiceButton = styled.button`
  background-color: transparent;
  color: #ffc107;
  border: 2px solid #ffc107;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #ffc107;
    color: white;
  }
`;

const AboutSection = styled(Section)`
  background-color: #f8f9fa;
`;

const AboutContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 3rem;
`;

const AboutImage = styled.div`
  flex: 1;
  min-width: 300px;
  height: 400px;
  background-image: url('/servicios/tipo-cliente/constructora-about.webp');
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const AboutText = styled.div`
  flex: 1;
  min-width: 300px;
`;

const AboutTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 80px;
    height: 3px;
    background-color: #ffc107;
  }
`;

const AboutParagraph = styled.p`
  color: #666;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 2rem;
`;

const StatItem = styled.div`
  flex: 1;
  min-width: 120px;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffc107;
  margin-bottom: 0.5rem;
`;

const StatText = styled.div`
  color: #666;
  font-size: 1rem;
`;

const ProjectsSection = styled(Section)`
  background-color: #333;
  color: white;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
`;

const ProjectCard = styled.div`
  position: relative;
  height: 300px;
  overflow: hidden;
  border-radius: 10px;
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &:hover .overlay {
    opacity: 1;
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const ProjectOverlay = styled.div.attrs({ className: 'overlay' })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const ProjectTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const ProjectCategory = styled.p`
  color: #ffc107;
  margin-bottom: 1rem;
`;

const ContactSection = styled(Section)``;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ContactCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ContactIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: #ffc107;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
`;

const ContactContent = styled.div`
  flex: 1;
`;

const ContactTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const ContactText = styled.p`
  color: #666;
  line-height: 1.6;
`;

const ContactForm = styled.form`
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #ffc107;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  resize: vertical;
  min-height: 150px;
  
  &:focus {
    outline: none;
    border-color: #ffc107;
  }
`;

const SubmitButton = styled.button`
  background-color: #ffc107;
  color: #333;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background-color: #e0a800;
  }
`;

const TestimonialsSection = styled(Section)`
  background-color: #f8f9fa;
`;

const TestimonialCard = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const TestimonialText = styled.p`
  color: #666;
  font-style: italic;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #eee;
  background-size: cover;
  background-position: center;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.h4`
  margin: 0 0 0.25rem;
`;

const AuthorRole = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin: 0;
`;

const AuthorRating = styled.div`
  color: #ffc107;
  font-size: 0.9rem;
  margin-top: 0.25rem;
`;

const Footer = styled.footer`
  background-color: #333;
  color: white;
  padding: 2rem;
  text-align: center;
  
  p {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const ConstructoraDemoPage: React.FC = () => {
  return (
    <>
      <SEO 
        title="Demo de Sitio Web para Constructoras | Web a Bajo Coste"
        description="Visualiza cómo podría ser tu página web de construcción con este ejemplo interactivo. Diseño profesional para mostrar tus proyectos y servicios."
        keywords="demo web constructora, diseño web construcción, ejemplo web constructora, página web construcción"
        canonical="https://webabajocoste.es/plantillas/constructora"
      >
        <meta name="robots" content="noindex, nofollow" />
      </SEO>
      
      <DemoViewer
        title="Demo Web para Constructoras"
        subtitle="Visualiza cómo podría ser tu nueva web para tu empresa constructora, diseñada para atraer nuevos clientes y mostrar tus proyectos de forma profesional"
        domainName="https://construye-futuro.es"
        serviceLink="/servicios/diseno-web-constructoras"
        serviceName="Constructoras"
      >
        <ConstructoraDemo>
          <HeroSection>
            <HeroContent>
              <CompanyName>Construye Futuro</CompanyName>
              <HeroTitle>Construimos el futuro, un proyecto a la vez</HeroTitle>
              <HeroSubtitle>Empresa constructora con más de 20 años de experiencia en todo tipo de proyectos residenciales, comerciales e industriales.</HeroSubtitle>
              <HeroButton>Solicitar presupuesto</HeroButton>
            </HeroContent>
          </HeroSection>
          
          <Section>
            <Container>
              <SectionTitle>Nuestros Servicios</SectionTitle>
              
              <ServicesGrid>
                <ServiceCard>
                  <ServiceImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/construccion-residencial.webp")' }} />
                  <ServiceContent>
                    <ServiceIcon>
                      <FaHome />
                    </ServiceIcon>
                    <ServiceTitle>Construcción Residencial</ServiceTitle>
                    <ServiceDescription>
                      Construimos viviendas unifamiliares, edificios residenciales y reformas integrales con los más altos estándares de calidad y eficiencia energética.
                    </ServiceDescription>
                    <ServiceButton>Más información</ServiceButton>
                  </ServiceContent>
                </ServiceCard>
                
                <ServiceCard>
                  <ServiceImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/construccion-comercial.webp")' }} />
                  <ServiceContent>
                    <ServiceIcon>
                      <FaBuilding />
                    </ServiceIcon>
                    <ServiceTitle>Construcción Comercial</ServiceTitle>
                    <ServiceDescription>
                      Desarrollamos proyectos comerciales que incluyen oficinas, locales, hoteles y centros comerciales, adaptándonos a las necesidades específicas de cada cliente.
                    </ServiceDescription>
                    <ServiceButton>Más información</ServiceButton>
                  </ServiceContent>
                </ServiceCard>
                
                <ServiceCard>
                  <ServiceImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/construccion-industrial.webp")' }} />
                  <ServiceContent>
                    <ServiceIcon>
                      <FaIndustry />
                    </ServiceIcon>
                    <ServiceTitle>Construcción Industrial</ServiceTitle>
                    <ServiceDescription>
                      Especialistas en la construcción de naves industriales, plantas de producción y almacenes logísticos, con soluciones eficientes y duraderas.
                    </ServiceDescription>
                    <ServiceButton>Más información</ServiceButton>
                  </ServiceContent>
                </ServiceCard>
              </ServicesGrid>
            </Container>
          </Section>
          
          <AboutSection>
            <Container>
              <AboutContent>
                <AboutImage />
                
                <AboutText>
                  <AboutTitle>Sobre Nosotros</AboutTitle>
                  <AboutParagraph>
                    En Construye Futuro llevamos más de dos décadas liderando el sector de la construcción con proyectos que destacan por su calidad, innovación y compromiso con el cliente.
                  </AboutParagraph>
                  <AboutParagraph>
                    Nuestro equipo está formado por profesionales altamente cualificados, que combinan experiencia y conocimientos técnicos para ofrecer soluciones a medida en cada proyecto.
                  </AboutParagraph>
                  <AboutParagraph>
                    Nos distinguimos por nuestra capacidad para ejecutar proyectos de alta complejidad, cumpliendo plazos y presupuestos sin comprometer la calidad.
                  </AboutParagraph>
                  
                  <Stats>
                    <StatItem>
                      <StatNumber>20+</StatNumber>
                      <StatText>Años de experiencia</StatText>
                    </StatItem>
                    <StatItem>
                      <StatNumber>450+</StatNumber>
                      <StatText>Proyectos completados</StatText>
                    </StatItem>
                    <StatItem>
                      <StatNumber>50+</StatNumber>
                      <StatText>Profesionales</StatText>
                    </StatItem>
                  </Stats>
                </AboutText>
              </AboutContent>
            </Container>
          </AboutSection>
          
          <ProjectsSection>
            <Container>
              <SectionTitle>Proyectos Destacados</SectionTitle>
              
              <ProjectsGrid>
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/proyecto-construccion1.webp" alt="Edificio Residencial" />
                  <ProjectOverlay>
                    <ProjectTitle>Torres Mirador</ProjectTitle>
                    <ProjectCategory>Residencial</ProjectCategory>
                    <ServiceButton>Ver detalles</ServiceButton>
                  </ProjectOverlay>
                </ProjectCard>
                
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/proyecto-construccion2.webp" alt="Centro Comercial" />
                  <ProjectOverlay>
                    <ProjectTitle>Centro Comercial Plaza</ProjectTitle>
                    <ProjectCategory>Comercial</ProjectCategory>
                    <ServiceButton>Ver detalles</ServiceButton>
                  </ProjectOverlay>
                </ProjectCard>
                
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/proyecto-construccion3.webp" alt="Nave Industrial" />
                  <ProjectOverlay>
                    <ProjectTitle>Parque Logístico Norte</ProjectTitle>
                    <ProjectCategory>Industrial</ProjectCategory>
                    <ServiceButton>Ver detalles</ServiceButton>
                  </ProjectOverlay>
                </ProjectCard>
                
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/proyecto-construccion4.webp" alt="Oficinas Modernas" />
                  <ProjectOverlay>
                    <ProjectTitle>Oficinas Panorama</ProjectTitle>
                    <ProjectCategory>Corporativo</ProjectCategory>
                    <ServiceButton>Ver detalles</ServiceButton>
                  </ProjectOverlay>
                </ProjectCard>
              </ProjectsGrid>
            </Container>
          </ProjectsSection>
          
          <TestimonialsSection>
            <Container>
              <SectionTitle>Testimonios</SectionTitle>
              
              <TestimonialCard>
                <TestimonialText>
                  "Construye Futuro ha superado todas nuestras expectativas. El proyecto se entregó a tiempo y con una calidad excepcional. Su equipo técnico supo resolver todos los desafíos que surgieron durante la construcción de nuestras nuevas oficinas."
                </TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar style={{ backgroundImage: 'url("/servicios/tipo-cliente/cliente-constructora1.webp")' }} />
                  <AuthorInfo>
                    <AuthorName>Antonio Martínez</AuthorName>
                    <AuthorRole>Director de Expansión, Grupo InverCap</AuthorRole>
                    <AuthorRating>
                      <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                    </AuthorRating>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
              
              <TestimonialCard>
                <TestimonialText>
                  "Elegimos a Construye Futuro para nuestro proyecto de centro comercial por su trayectoria y no nos arrepentimos. La obra se completó dentro del presupuesto acordado y la comunicación durante todo el proceso fue impecable."
                </TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar style={{ backgroundImage: 'url("/servicios/tipo-cliente/cliente-constructora2.webp")' }} />
                  <AuthorInfo>
                    <AuthorName>María González</AuthorName>
                    <AuthorRole>CEO, Desarrollos Urbanos S.L.</AuthorRole>
                    <AuthorRating>
                      <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                    </AuthorRating>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            </Container>
          </TestimonialsSection>
          
          <ContactSection>
            <Container>
              <SectionTitle>Contacto</SectionTitle>
              
              <ContactGrid>
                <ContactInfo>
                  <ContactCard>
                    <ContactIcon>
                      <FaMapMarkerAlt />
                    </ContactIcon>
                    <ContactContent>
                      <ContactTitle>Dirección</ContactTitle>
                      <ContactText>
                        Avenida de la Industria, 45<br />
                        Polígono Industrial El Progreso<br />
                        28938 Móstoles, Madrid
                      </ContactText>
                    </ContactContent>
                  </ContactCard>
                  
                  <ContactCard>
                    <ContactIcon>
                      <FaPhone />
                    </ContactIcon>
                    <ContactContent>
                      <ContactTitle>Teléfono</ContactTitle>
                      <ContactText>
                        +34 916 234 567<br />
                        +34 600 123 456
                      </ContactText>
                    </ContactContent>
                  </ContactCard>
                  
                  <ContactCard>
                    <ContactIcon>
                      <FaEnvelope />
                    </ContactIcon>
                    <ContactContent>
                      <ContactTitle>Email</ContactTitle>
                      <ContactText>
                        info@construye-futuro.es<br />
                        presupuestos@construye-futuro.es
                      </ContactText>
                    </ContactContent>
                  </ContactCard>
                  
                  <ContactCard>
                    <ContactIcon>
                      <FaCalendarAlt />
                    </ContactIcon>
                    <ContactContent>
                      <ContactTitle>Horario</ContactTitle>
                      <ContactText>
                        Lunes a Viernes: 8:00 - 18:00<br />
                        Sábados: 9:00 - 13:00
                      </ContactText>
                    </ContactContent>
                  </ContactCard>
                </ContactInfo>
                
                <ContactForm>
                  <FormGroup>
                    <FormLabel>Nombre</FormLabel>
                    <FormInput type="text" placeholder="Tu nombre" />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel>Email</FormLabel>
                    <FormInput type="email" placeholder="Tu email" />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel>Teléfono</FormLabel>
                    <FormInput type="tel" placeholder="Tu teléfono" />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel>Mensaje</FormLabel>
                    <FormTextarea placeholder="Describe tu proyecto o consulta" />
                  </FormGroup>
                  
                  <SubmitButton>Enviar Mensaje</SubmitButton>
                </ContactForm>
              </ContactGrid>
            </Container>
          </ContactSection>
          
          <Footer>
            <p>© 2023 Construye Futuro | Todos los derechos reservados</p>
          </Footer>
        </ConstructoraDemo>
      </DemoViewer>
    </>
  );
};

export default ConstructoraDemoPage; 